import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Plus, Minus, X, Rows4, Users, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { decodedToken } from '../../utils/auth';
import {
  getLightLeakageRecordsByEmail,
  getAllLightLeakageRecords,
  acceptLightLeakageRecord,
  rejectLightLeakageRecord,
} from '../../services/apiService';

const apiService = {
  getLightLeakageRecordsByEmail,
  getAllLightLeakageRecords,
  acceptLightLeakageRecord,
  rejectLightLeakageRecord,
};

const ProductInfoInput = () => {
  const navigate = useNavigate();
  const previewCanvasRef = useRef(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewDefects, setPreviewDefects] = useState([]);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [customRatio, setCustomRatio] = useState('');
  const [customRatios, setCustomRatios] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);

  const defaultState = {
    productType: 'TV',
    modelName: '',
    hasBezel: null,
    leakageRatio: null,
    dimensions: {
      width: { whole: 40, decimal: 0 },
      height: { whole: 40, decimal: 0 },
    },
  };

  const [productType, setProductType] = useState(defaultState.productType);
  const [modelName, setModelName] = useState(defaultState.modelName);
  const [hasBezel, setHasBezel] = useState(defaultState.hasBezel);
  const [leakageRatio, setLeakageRatio] = useState(defaultState.leakageRatio);
  const [dimensions, setDimensions] = useState(defaultState.dimensions);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [products, setProducts] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewType, setPreviewType] = useState('');
  const [error, setError] = useState(null);

  const email = localStorage.getItem('email');

  // Color mapping for light leakage types
  const colors = {
    pressing: '#FF0000', // Red
    dark_state: '#00FF00', // Green
  };

  // Load productInfo from localStorage on mount
  useEffect(() => {
    const decode = decodedToken();
    setIsAdmin(decode?.userType === 'powerAdmin');
    setIsLoaded(true);

    const savedProductInfo = localStorage.getItem('productInfo');
    if (savedProductInfo) {
      const parsedProductInfo = JSON.parse(savedProductInfo);
      setProductType(parsedProductInfo.productType || defaultState.productType);
      setModelName(parsedProductInfo.modelName || defaultState.modelName);
      setHasBezel(
        parsedProductInfo.bezel === 'yes'
          ? true
          : parsedProductInfo.bezel === 'no'
          ? false
          : defaultState.hasBezel
      );
      setLeakageRatio(parsedProductInfo.lightLeakageRatioCriteria || defaultState.leakageRatio);
      setDimensions(parsedProductInfo.dimensions || defaultState.dimensions);
    }
  }, []);

  const predefinedRatios = {
    TV: [1.6, 1.8],
    PD: [1.6, 1.8],
    MNT: [1.0],
  };

  const resetForm = () => {
    setProductType(defaultState.productType);
    setModelName(defaultState.modelName);
    setHasBezel(defaultState.hasBezel);
    setLeakageRatio(defaultState.leakageRatio);
    setDimensions(defaultState.dimensions);
    localStorage.removeItem('productInfo');
  };

  const isFormValid = () => {
    return (
      modelName.trim() !== '' &&
      hasBezel !== null &&
      leakageRatio !== null &&
      dimensions.width.whole > 0 &&
      dimensions.height.whole > 0
    );
  };

  const handleBack = () => {
    resetForm(); // Clear form data
    localStorage.removeItem('productInfo'); // Clear localStorage
    navigate('/dashboard');
  };

  const handleDimensionChange = (dimension, part, change) => {
    setDimensions((prev) => {
      const newValue = { ...prev[dimension] };
      if (part === 'whole') {
        newValue.whole = Math.max(0, newValue.whole + change);
      } else if (part === 'decimal') {
        let newDecimal = newValue.decimal + change;
        if (newDecimal < 0) {
          newDecimal = 9;
          newValue.whole = Math.max(0, newValue.whole - 1);
        } else if (newDecimal > 9) {
          newDecimal = 0;
          newValue.whole += 1;
        }
        newValue.decimal = newDecimal;
      }
      return { ...prev, [dimension]: newValue };
    });
  };

  const handleCustomRatioChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value) && value <= 10) {
      setCustomRatio(value);
    }
  };

  const addCustomRatio = () => {
    if (customRatio && !customRatios.includes(parseFloat(customRatio))) {
      setCustomRatios([...customRatios, parseFloat(customRatio)]);
      setCustomRatio('');
    }
  };

  const fetchMyProducts = async () => {
    setIsLoading(true);
    setPreviewType('my');
    try {
      const data = await apiService.getLightLeakageRecordsByEmail(email);
      const acceptedProducts = data.data.filter((product) => product.status === 'accepted');
      console.log('Fetched my accepted products:', JSON.stringify(acceptedProducts, null, 2));
      setProducts(acceptedProducts);
      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching my products:', error);
      Swal.fire('Error!', error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    setIsLoading(true);
    setPreviewType('all');
    try {
      const data = await apiService.getAllLightLeakageRecords();
      const pendingProducts = data
        .filter((product) => product.status === null || product.status === 'pending')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort by createdAt, newest first
      console.log('Fetched all pending products:', JSON.stringify(pendingProducts, null, 2));
      setProducts(pendingProducts);
      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching all products:', error);
      Swal.fire('Error!', error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptProduct = async (productId) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You are about to accept this product record.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, accept it!',
      });

      if (result.isConfirmed) {
        await apiService.acceptLightLeakageRecord(productId);
        Swal.fire('Accepted!', 'The product record has been accepted.', 'success');
        if (previewType === 'all') {
          await fetchAllProducts();
        } else {
          await fetchMyProducts();
        }
      }
    } catch (error) {
      console.error('Error accepting product:', error);
      Swal.fire('Error!', error.message || 'Failed to accept product record', 'error');
    }
  };

  const handleRejectProduct = async (productId) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You are about to reject and permanently delete this product record!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, reject it!',
      });

      if (result.isConfirmed) {
        await apiService.rejectLightLeakageRecord(productId);
        Swal.fire('Rejected!', 'The product record has been rejected and deleted.', 'success');
        if (previewType === 'all') {
          await fetchAllProducts();
        } else {
          await fetchMyProducts();
        }
      }
    } catch (error) {
      console.error('Error rejecting product:', error);
      Swal.fire('Error!', error.message || 'Failed to reject product record', 'error');
    }
  };

  const handlePreviewClick = (imageUrl, defects) => {
    console.log('Preview image URL:', imageUrl);
    console.log('Preview defects:', JSON.stringify(defects, null, 2));
    setPreviewImageUrl(imageUrl);
    setPreviewDefects(defects || []);
    setShowImagePreview(true);
    setImageLoading(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImageUrl(null);
    setPreviewDefects([]);
    setImageLoading(false);
  };

  const renderLightLeakageRecordings = (defects) => {
    if (!defects || !Array.isArray(defects) || defects.length === 0) {
      return 'No light leakage recordings';
    }
    return defects
      .map((defect) => {
        const lightLeakageType = defect.lightLeakageType || 'unknown';
        const lightLeakageLabId = defect.lightLeakageLabId || 'unknown';
        const rootCause = defect.lightLeakageLabelIssue?.lightLeakageRootCause || '';
        return `ID: ${lightLeakageLabId} | ${lightLeakageType}${rootCause ? ` : ${rootCause}` : ''}`;
      })
      .join('\n') || 'No valid light leakage recordings';
  };

  useEffect(() => {
    if (showImagePreview && previewImageUrl && previewCanvasRef.current) {
      const canvas = previewCanvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get 2D context for preview canvas');
        return;
      }

      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.style.width = '';
        canvas.style.height = '';
        console.log(`Image loaded: ${img.naturalWidth}x${img.naturalHeight}`);

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

        if (previewDefects && Array.isArray(previewDefects)) {
          previewDefects.forEach((defect, index) => {
            if (defect.bbox && Array.isArray(defect.bbox) && defect.bbox.length === 4) {
              const [center_x, center_y, width, height] = defect.bbox;
              console.log(`Defect ${index} bbox: [${center_x}, ${center_y}, ${width}, ${height}]`);

              const x = (center_x - width / 2) * img.naturalWidth;
              const y = (center_y - height / 2) * img.naturalHeight;
              const pixelWidth = width * img.naturalWidth;
              const pixelHeight = height * img.naturalHeight;
              console.log(`Defect ${index} pixel coords: x=${x}, y=${y}, width=${pixelWidth}, height=${pixelHeight}`);

              const lightLeakageTypeLower = defect.lightLeakageType?.toLowerCase() || 'unknown';
              const color = colors[lightLeakageTypeLower] || '#FFFFFF';
              ctx.strokeStyle = color;
              ctx.lineWidth = 2;
              ctx.strokeRect(x, y, pixelWidth, pixelHeight);

              if (defect.lightLeakageType && defect.lightLeakageLabId) {
                ctx.fillStyle = color;
                ctx.font = '14px Arial';
                const label = `ID: ${defect.lightLeakageLabId} | ${defect.lightLeakageType}`;
                ctx.fillText(label, x, y - 5);
              }
            } else {
              console.warn(`Defect ${index} has invalid bbox:`, defect.bbox);
            }
          });
        }
        setImageLoading(false);
      };

      img.onerror = () => {
        console.error('Failed to load image:', previewImageUrl);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load image for preview. Please check if the image exists or contact support.',
          icon: 'error',
          confirmButtonText: 'Retry',
          showCancelButton: true,
        }).then((result) => {
          if (result.isConfirmed) {
            img.src = previewImageUrl + '?' + new Date().getTime();
          } else {
            closeImagePreview();
          }
        });
        setImageLoading(false);
      };

      console.log('Attempting to load image:', previewImageUrl);
      img.src = previewImageUrl + '?' + new Date().getTime();
    }
  }, [showImagePreview, previewImageUrl, previewDefects]);

  const handleSave = () => {
    if (!isFormValid()) {
      setError('Please fill all required fields');
      return;
    }

    const productInfo = {
      productType,
      modelName,
      bezel: hasBezel ? 'yes' : 'no',
      lightLeakageRatioCriteria: leakageRatio,
      dimensions: {
        width: { whole: dimensions.width.whole, decimal: dimensions.width.decimal },
        height: { whole: dimensions.height.whole, decimal: dimensions.height.decimal },
      },
    };

    localStorage.setItem('productInfo', JSON.stringify(productInfo));
    navigate('/light-leakage-detector-camera', { state: { productInfo } });
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="flex items-center mb-6">
        <button className="mr-4" onClick={handleBack}>
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-medium">Product Info Input</h1>
        <div className="ml-auto flex space-x-2">
          <button
            className="flex items-center text-white py-0.5 px-2 rounded-lg disabled:bg-gray-600 cursor-pointer bg-gray-700 hover:bg-gray-600"
            onClick={fetchMyProducts}
            disabled={isLoading}
            title="View My Accepted Products"
          >
            {isLoading && previewType === 'my' ? 'Loading...' : <Rows4 className="w-5 h-5" />}
          </button>
          {isAdmin && (
            <button
              className="flex items-center text-white py-0.5 px-2 rounded-lg disabled:bg-gray-600 cursor-pointer bg-gray-700 hover:bg-gray-600"
              onClick={fetchAllProducts}
              disabled={isLoading}
              title="View Pending Products (Admin Only)"
            >
              {isLoading && previewType === 'all' ? 'Loading...' : <Users className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {showImagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-60 overflow-auto p-4">
          <div className="relative w-full h-full flex items-center justify-center">
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
            <canvas
              ref={previewCanvasRef}
              className={`object-contain max-w-full max-h-full ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
            />
            <button
              onClick={closeImagePreview}
              className="absolute top-4 right-4 text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700 transition-colors z-70"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {showPreview && products && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gray-900 px-6 py-4 flex justify-between items-center border-b border-gray-700">
              <h2 className="text-2xl font-semibold text-white">
                {previewType === 'all' ? 'Pending Products (Admin View)' : 'My Accepted Products'}
              </h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {products.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  {previewType === 'all' ? 'No pending products awaiting approval' : 'No accepted products found'}
                </div>
              ) : (
                <div className="grid gap-6">
                  {products.map((product) => (
                    <div
                      key={product._id}
                      className="bg-gray-900 rounded-lg p-5 hover:shadow-lg transition-shadow duration-200 border border-gray-700"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h3 className="text-xl font-medium text-blue-400 border-b border-gray-700 pb-2">
                            Product Information
                          </h3>
                          <div className="space-y-2 text-gray-200">
                            <div className="flex justify-between">
                              <span className="text-gray-400">Type:</span>
                              <span>{product.productInfo.productType}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Model:</span>
                              <span>{product.productInfo.modelName}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Bezel:</span>
                              <span>{product.productInfo.bezel}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Leakage Ratio:</span>
                              <span>{product.productInfo.lightLeakageRatioCriteria}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Dimensions:</span>
                              <span>{`${product.productInfo.dimensions?.width?.whole || 0}.${product.productInfo.dimensions?.width?.decimal || 0} × ${product.productInfo.dimensions?.height?.whole || 0}.${product.productInfo.dimensions?.height?.decimal || 0} cm`}</span>
                            </div>
                            {isAdmin && previewType === 'all' && (
                              <div className="flex justify-between">
                                <span className="text-gray-400">Uploaded by:</span>
                                <span>{product.email}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-gray-400">Status:</span>
                              <span
                                className={
                                  product.status === 'accepted'
                                    ? 'text-green-400'
                                    : product.status === 'rejected'
                                    ? 'text-red-400'
                                    : 'text-yellow-400'
                                }
                              >
                                {product.status === null
                                  ? 'Pending'
                                  : product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                              </span>
                            </div>
                            <div className="space-y-1">
                              <span className="text-gray-400">Light Leakage Recordings:</span>
                              <pre className="bg-gray-800 p-2 rounded text-sm text-gray-200 whitespace-pre-wrap">
                                {renderLightLeakageRecordings(product.lightLeakageRecording?.lightLeakageLabeling?.defects)}
                              </pre>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <h3 className="text-xl font-medium text-blue-400 border-b border-gray-700 pb-2">
                            Defect Image
                          </h3>
                          <div className="space-y-2">
                            <button
                              onClick={() =>
                                handlePreviewClick(
                                  `${product.lightLeakageRecording.rawimageUrl}`,
                                  product.lightLeakageRecording?.lightLeakageLabeling?.defects
                                )
                              }
                              className="flex items-center justify-center w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              <Eye className="w-5 h-5 mr-2" /> Preview
                            </button>
                          </div>
                        </div>
                      </div>
                      {isAdmin && (product.status === null || product.status === 'pending') && (
                        <div className="mt-4 flex justify-end space-x-4">
                          <button
                            onClick={() => handleAcceptProduct(product._id)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleRejectProduct(product._id)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label className="block mb-3">Product Type</label>
          <div className="relative">
            <div className="flex w-full">
              {['TV', 'MNT', 'PD'].map((type) => (
                <button
                  key={type}
                  onClick={() => setProductType(type)}
                  className={`flex-1 py-2 text-center relative ${
                    productType === type ? 'text-blue-500' : 'text-gray-400'
                  }`}
                >
                  {type}
                  {productType === type && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500" />
                  )}
                </button>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 w-full h-px bg-gray-800" />
          </div>
        </div>

        <div>
          <label className="block mb-3">Model Name</label>
          <input
            type="text"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            placeholder="Enter model name"
            className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white placeholder-gray-400"
          />
        </div>

        <div>
          <label className="block mb-3">Bezel</label>
          <div className="flex space-x-4">
            <button
              onClick={() => setHasBezel(true)}
              className={`px-6 py-2 rounded-full ${
                hasBezel === true ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
              }`}
            >
              Yes
            </button>
            <button
              onClick={() => setHasBezel(false)}
              className={`px-6 py-2 rounded-full ${
                hasBezel === false ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
              }`}
            >
              No
            </button>
          </div>
        </div>

        <div>
          <label className="block mb-3">Light Leakage Ratio Criteria</label>
          <div className="flex flex-wrap gap-4">
            {predefinedRatios[productType].map((ratio) => (
              <button
                key={ratio}
                onClick={() => setLeakageRatio(ratio)}
                className={`px-6 py-2 rounded-full ${
                  leakageRatio === ratio ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                {ratio === 1.0 ? 'No Leakage (1.0)' : ratio}
              </button>
            ))}
            {productType === 'MNT' &&
              [1.6, 1.8].map((ratio) => (
                <button
                  key={ratio}
                  className="px-6 py-2 rounded-full bg-gray-600 text-gray-400 cursor-not-allowed"
                  disabled
                >
                  {ratio}
                </button>
              ))}
            {customRatios.map((ratio) => (
              <button
                key={ratio}
                onClick={() => setLeakageRatio(ratio)}
                className={`px-6 py-2 rounded-full relative ${
                  leakageRatio === ratio ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                {ratio}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCustomRatios(customRatios.filter((r) => r !== ratio));
                    if (leakageRatio === ratio) setLeakageRatio(null);
                  }}
                  className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                >
                  ×
                </button>
              </button>
            ))}
            {productType !== 'MNT' && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Custom (e.g., 2.0)"
                  value={customRatio}
                  onChange={handleCustomRatioChange}
                  className="bg-gray-800 rounded-lg px-4 py-2 text-white placeholder-gray-400 w-32"
                />
                <button
                  onClick={addCustomRatio}
                  className={`p-2 rounded-full ${
                    customRatio &&
                    !customRatios.includes(parseFloat(customRatio)) &&
                    parseFloat(customRatio) > 0
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-600 cursor-not-allowed'
                  }`}
                  disabled={
                    !customRatio ||
                    customRatios.includes(parseFloat(customRatio)) ||
                    parseFloat(customRatio) <= 0
                  }
                >
                  <Plus className="w-5 h-5 text-white" />
                </button>
              </div>
            )}
          </div>
        </div>

        {['width', 'height'].map((dimension) => (
          <div key={dimension}>
            <label className="block mb-3 capitalize">
              {dimension}:{' '}
              <span className="text-gray-400">
                {dimensions[dimension].whole}.{dimensions[dimension].decimal}cm
              </span>
            </label>
            <div className="flex items-center space-x-4">
              <div className="flex items-center bg-gray-800 rounded-lg">
                <button
                  className="p-2 text-gray-400 hover:text-white"
                  onClick={() => handleDimensionChange(dimension, 'whole', -1)}
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span className="px-4">{dimensions[dimension].whole}</span>
                <button
                  className="p-2 text-gray-400 hover:text-white"
                  onClick={() => handleDimensionChange(dimension, 'whole', 1)}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center bg-gray-800 rounded-lg">
                <button
                  className="p-2 text-gray-400 hover:text-white"
                  onClick={() => handleDimensionChange(dimension, 'decimal', -1)}
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span className="px-4">.{dimensions[dimension].decimal}</span>
                <button
                  className="p-2 text-gray-400 hover:text-white"
                  onClick={() => handleDimensionChange(dimension, 'decimal', 1)}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {error && <div className="text-red-500 text-sm">{error}</div>}

        <button
          onClick={handleSave}
          disabled={!isFormValid()}
          className={`w-full py-3 rounded-lg mt-6 ${
            isFormValid() ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          Save and Proceed
        </button>
      </div>
    </div>
  );
};

export default ProductInfoInput;